/**
 * Tabular Format Detection
 *
 * Shared, quote-aware format detection module. Scores candidate delimiters
 * by parsing preview lines with Papa.parse and comparing column consistency.
 *
 * Pure function — no React, no tool/detector/store dependencies.
 *
 * @see .squad/decisions/inbox/mal-table-architecture.md §2.3
 */

import * as Papa from 'papaparse';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type DelimiterName = 'comma' | 'tab' | 'pipe' | 'semicolon';
export type FormatLabel = 'CSV' | 'TSV' | 'PIPE' | 'SEMICOLON';

export interface TabularFormatInfo {
  /** The winning delimiter character */
  delimiter: string;
  /** Human-readable name */
  delimiterName: DelimiterName;
  /** Format label for display */
  formatLabel: FormatLabel;
  /** Total rows parsed (including header) */
  rowCount: number;
  /** Column count (mode across rows) */
  columnCount: number;
  /** Whether all rows have the same column count */
  hasConsistentColumns: boolean;
  /** Confidence score 0–100 */
  confidence: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Candidate definitions (tie-break order: comma > tab > pipe > semicolon)
// ─────────────────────────────────────────────────────────────────────────────

interface Candidate {
  char: string;
  name: DelimiterName;
  label: FormatLabel;
}

const CANDIDATES: Candidate[] = [
  { char: ',', name: 'comma', label: 'CSV' },
  { char: '\t', name: 'tab', label: 'TSV' },
  { char: '|', name: 'pipe', label: 'PIPE' },
  { char: ';', name: 'semicolon', label: 'SEMICOLON' },
];

const MAX_PREVIEW_LINES = 20;

// ─────────────────────────────────────────────────────────────────────────────
// Guard-rail checks
// ─────────────────────────────────────────────────────────────────────────────

/** Reject structured/markup formats that should never be treated as tabular. */
function looksLikeNonTabularFormat(trimmed: string): boolean {
  // YAML front-matter
  if (trimmed.startsWith('---')) return true;

  // TOML section header — must be a standalone line (no delimiters around it)
  if (/^(\[[\w.-]+\])\s*$/m.test(trimmed)) return true;

  // Structural XML/HTML check: multiple lines that look like markup tags
  if (/^<\w/m.test(trimmed)) {
    const lines = trimmed.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const tagLines = lines.filter((l) => /^\s*<\/?[\w!]/.test(l));
    if (tagLines.length >= lines.length * 0.5 && tagLines.length >= 2) return true;
  }

  // JSON-ish: starts with { or [ and contains "key": patterns
  if (looksLikeJSON(trimmed)) return true;

  return false;
}

/**
 * Reject JSON data before delimiter scoring. Comma-rich JSON objects/arrays
 * can score highly as CSV when columns happen to be consistent across lines.
 */
function looksLikeJSON(trimmed: string): boolean {
  const firstChar = trimmed[0];
  if (firstChar !== '{' && firstChar !== '[') return false;

  // Fast path: short input that parses as valid JSON
  if (trimmed.length <= 500) {
    try {
      JSON.parse(trimmed);
      return true;
    } catch { /* not valid JSON — fall through to heuristic */ }
  }

  // Structural heuristic: "key": pattern typical of JSON objects/arrays
  const preview = trimmed.slice(0, 500);
  return /"[^"]*"\s*:/.test(preview);
}

/**
 * Detect Markdown tables: pipe-delimited rows with a separator row
 * containing dashes (optionally with colons for alignment).
 * Pattern: `| --- |` or `| :---: |` etc.
 */
function looksLikeMarkdownTable(text: string): boolean {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  for (const line of lines) {
    const trimmed = line.trim();
    // Markdown separator row: pipes surrounding cells of dashes/colons
    if (/^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(trimmed)) {
      return true;
    }
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoring
// ─────────────────────────────────────────────────────────────────────────────

interface CandidateScore {
  candidate: Candidate;
  columnCount: number;
  rowCount: number;
  hasConsistentColumns: boolean;
  consistencyRatio: number;
  score: number;
}

function scoreCandidate(input: string, candidate: Candidate): CandidateScore {
  const result = Papa.parse<string[]>(input, {
    delimiter: candidate.char,
    header: false,
    skipEmptyLines: false,
    preview: MAX_PREVIEW_LINES,
  });

  const allRows = result.data ?? [];
  // Use non-empty rows for scoring consistency
  const rows = allRows.filter((row) =>
    row.some((cell) => cell.trim() !== ''),
  );

  if (rows.length < 2) {
    return { candidate, columnCount: 0, rowCount: allRows.length, hasConsistentColumns: false, consistencyRatio: 0, score: 0 };
  }

  const colCounts = rows.map((r) => r.length);

  // Statistical mode of column counts (most frequent width)
  const colCountFreq = new Map<number, number>();
  for (const c of colCounts) {
    colCountFreq.set(c, (colCountFreq.get(c) ?? 0) + 1);
  }
  let modeCols = colCounts[0];
  let modeFreq = 0;
  for (const [cols, freq] of colCountFreq) {
    if (freq > modeFreq || (freq === modeFreq && cols < modeCols)) {
      modeCols = cols;
      modeFreq = freq;
    }
  }

  if (modeCols < 2) {
    return { candidate, columnCount: modeCols, rowCount: allRows.length, hasConsistentColumns: false, consistencyRatio: 0, score: 0 };
  }

  const modeCount = colCounts.filter((c) => c === modeCols).length;
  const consistencyRatio = modeCount / rows.length;
  const hasConsistentColumns = colCounts.every((c) => c === modeCols);

  // Reject if all cells are empty
  const allEmpty = rows.every((r) => r.every((c) => !c.trim()));
  if (allEmpty) {
    return { candidate, columnCount: modeCols, rowCount: allRows.length, hasConsistentColumns, consistencyRatio: 0, score: 0 };
  }

  // Score: consistency × column count × row count (non-empty rows for scoring)
  const score = consistencyRatio * modeCols * rows.length;

  return { candidate, columnCount: modeCols, rowCount: allRows.length, hasConsistentColumns, consistencyRatio, score };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Detect the most likely tabular format of the given input string.
 *
 * Parses preview lines with each candidate delimiter via Papa.parse,
 * scores by column consistency × width × depth, and returns the best match.
 *
 * Returns `null` if the input doesn't look tabular (< 2 rows, < 2 columns,
 * or matches a non-tabular format like JSON/XML/YAML/Markdown).
 */
export function detectTabularFormat(input: string): TabularFormatInfo | null {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim();

  // Guard rails: reject non-tabular formats
  if (looksLikeNonTabularFormat(trimmed)) return null;
  if (looksLikeMarkdownTable(trimmed)) return null;

  // Score all candidates
  const scores = CANDIDATES.map((c) => scoreCandidate(input, c));

  // Pick the best (candidates are in tie-break order for equal scores)
  let best: CandidateScore | null = null;
  for (const s of scores) {
    if (s.score <= 0) continue;
    if (!best || s.score > best.score) {
      best = s;
    }
  }

  if (!best || best.columnCount < 2 || best.rowCount < 2) return null;

  const confidence = Math.round(Math.min(100, best.consistencyRatio * 100));

  return {
    delimiter: best.candidate.char,
    delimiterName: best.candidate.name,
    formatLabel: best.candidate.label,
    rowCount: best.rowCount,
    columnCount: best.columnCount,
    hasConsistentColumns: best.hasConsistentColumns,
    confidence,
  };
}

/** Map a TabularFormat config value to its delimiter character */
export function formatToDelimiter(format: string): string | undefined {
  switch (format) {
    case 'csv': return ',';
    case 'tsv': return '\t';
    case 'pipe': return '|';
    case 'semicolon': return ';';
    default: return undefined;
  }
}

/** Map a delimiter character to a FormatLabel */
export function delimiterToLabel(delimiter: string): FormatLabel {
  switch (delimiter) {
    case '\t': return 'TSV';
    case '|': return 'PIPE';
    case ';': return 'SEMICOLON';
    default: return 'CSV';
  }
}
